import {
  ensureInitialized,
  fetchAndActivate,
  getRemoteConfig,
  isSupported,
  RemoteConfig,
  setCustomSignals,
} from 'firebase/remote-config';
import { version } from 'shared/constants';
import {
  getRemoteConfigFetchVersion,
  setRemoteConfigFetchVersion,
} from 'shared/services/local-storage';

import { initApp } from './init-app';
import { remoteAppConfigDefaults } from './remote-app-config-defaults';

let remoteConfig: RemoteConfig | undefined = undefined;

// Remote Config conditions serve a value per web version through this signal; a release without it gets the default.
const WEB_VERSION_SIGNAL = 'web_version';

// Undefined where IndexedDB cannot be opened: the SDK opens it as soon as the instance exists and leaves the rejection unhandled.
export const getRemoteAppConfig = async () => {
  if (!remoteConfig) {
    if (!(await isSupported())) {
      return undefined;
    }

    remoteConfig = getRemoteConfig(initApp());

    remoteConfig.defaultConfig = remoteAppConfigDefaults;

    if (import.meta.env.VITE_ENVIRONMENT !== 'production') {
      remoteConfig.settings.minimumFetchIntervalMillis = 1;
    }

    await setCustomSignals(remoteConfig, { [WEB_VERSION_SIGNAL]: version });

    // A new signal leaves the SDK's cached fetch in place for up to twelve hours, so a release's first load fetches past it.
    if (getRemoteConfigFetchVersion() !== version) {
      remoteConfig.settings.minimumFetchIntervalMillis = 0;
    }

    // Offline, Firebase Installations rejects a promise it only handles after an IndexedDB round trip.
    if (!navigator.onLine) {
      await ensureInitialized(remoteConfig);
      return remoteConfig;
    }

    await fetchAndActivate(remoteConfig);
    setRemoteConfigFetchVersion(version);
  }

  return remoteConfig;
};

export const clearRemoteAppConfig = () => {
  remoteConfig = undefined;
};
